import { useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { saveCurrentGameweek } from '../storage'
import { getGamesByGameweek, getActualResultsByGameweek } from '../seasonData'
import { useAutosave } from './usePersistedState'
import type { Game } from '../types'

export const STARTING_GAMEWEEK = 1

export interface UseGameweekStateOptions {
  /** Nothing is persisted until the initial load has landed. */
  isLoading: boolean
  onToast: (message: string) => void
  /**
   * Bumped whenever the fetched season store changes — a live fixture reaching
   * full time refreshes results outside React, and this is what makes the read below recompute.
   */
  seasonVersion?: number
}

export interface GameweekStateController {
  currentGameweek: number
  setCurrentGameweek: Dispatch<SetStateAction<number>>
  /** This gameweek's fixtures, with their real, shared kickoff times. */
  games: Game[]
  actualResults: Record<string, { homeScore: number; awayScore: number }>
  /** Every gameweek whose shared deadline has passed — from the server, not the local clock. */
  deadlinePassedGameweeks: number[]
  setDeadlinePassedGameweeks: Dispatch<SetStateAction<number[]>>
  /**
   * Every gameweek whose results are all in — the server's `resultsRevealed`, for every gameweek
   * rather than just the current one. Scoring is continuous now, so "this
   * gameweek has points" and "this gameweek is over" are different questions, and anything that
   * wants a *final* number rather than a live one has to ask this second one.
   */
  settledGameweeks: number[]
  setSettledGameweeks: Dispatch<SetStateAction<number[]>>
  /** Idempotently mark the current gameweek's deadline as passed, locally. */
  markDeadlinePassed: (gameweek: number) => void
  isDeadlinePassed: boolean
  resultsRevealed: boolean
  setResultsRevealed: Dispatch<SetStateAction<boolean>>
}

/**
 * Which gameweek is live, its shared phase (deadline passed / results
 * revealed — both admin-driven and server-held), and the fixtures + results that
 * follow from it.
 *
 * Kickoffs are the season's own, shared by every user — the per-user anchoring
 * this hook used to apply is gone. Everything downstream reads fixtures from `games`, so the
 * labels stay consistent.
 */
export function useGameweekState({
  isLoading,
  onToast,
  seasonVersion = 0,
}: UseGameweekStateOptions): GameweekStateController {
  void seasonVersion
  const [currentGameweek, setCurrentGameweek] = useState<number>(STARTING_GAMEWEEK)
  const [deadlinePassedGameweeks, setDeadlinePassedGameweeks] = useState<number[]>([])
  const [settledGameweeks, setSettledGameweeks] = useState<number[]>([])
  const [resultsRevealed, setResultsRevealed] = useState(false)

  // Records which gameweek this user is looking at. Nothing is anchored any more:
  // retired the per-user kickoff calendar, so there is no round-tripped moment to hold on to.
  useAutosave({
    value: currentGameweek,
    enabled: !isLoading,
    save: async (gameweek) => {
      await saveCurrentGameweek(gameweek)
    },
    onError: () => onToast('Failed to save your current gameweek.'),
  })

  // Real, shared kickoff times straight from the season store — one canonical calendar for
  // everyone.
  const games = getGamesByGameweek()[currentGameweek] ?? []

  return {
    currentGameweek,
    setCurrentGameweek,
    games,
    actualResults: getActualResultsByGameweek()[currentGameweek] ?? {},
    deadlinePassedGameweeks,
    setDeadlinePassedGameweeks,
    settledGameweeks,
    setSettledGameweeks,
    markDeadlinePassed: (gameweek) =>
      setDeadlinePassedGameweeks((prev) =>
        prev.includes(gameweek) ? prev : [...prev, gameweek]
      ),
    isDeadlinePassed: deadlinePassedGameweeks.includes(currentGameweek),
    resultsRevealed,
    setResultsRevealed,
  }
}
